/* eslint-disable global-require */
import React, { forwardRef, useContext, useImperativeHandle, useMemo, useState, useEffect, useCallback } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Map, MapProps, MapMouseEvent, AdvancedMarker, AdvancedMarkerProps, useMap, useApiLoadingStatus, APILoadingStatus } from '@vis.gl/react-google-maps'
import { Spinner, IconRegularError } from '@notino/react-styleguide'
import { type Marker, MarkerClusterer, MarkerUtils, Renderer } from '@googlemaps/markerclusterer'

// utils
import { MAP } from '../../utils/enums'
import { AppContext } from '../../utils/appProvider'
import { getIntl } from '../../utils/intl'

// styled
import * as SC from './GoogleMapsStyles'

// hooks
import useMessages from '../../hooks/useMessages'

// types
export type MapMarker<ExtraType extends Record<string, any> = {}> = {
	id: string
	position: AdvancedMarkerProps['position']
	extra: ExtraType
}

export type OnMarkerClickFunc<MarkerExtraType extends Record<string, any> = {}> = (
	markerElement: google.maps.Marker | google.maps.marker.AdvancedMarkerElement,
	data: MapMarker<MarkerExtraType>
) => void

type Props<MarkerExtraType extends Record<string, any> = {}> = MapProps & {
	markers?: MapMarker<MarkerExtraType>[]
	defaultSelectedMarkerID?: string
	onMarkerClick?: OnMarkerClickFunc<MarkerExtraType>
	markersLoading?: boolean
}

type Handle = {
	resetCurrentMarker: () => void
}

const ErrorComponent = () => {
	const { messages } = useMessages()
	return (
		<SC.ErrorContainer>
			<IconRegularError width={'2rem'} height={'2rem'} color={'text.primary'} />
			{messages['We are sorry. Google Maps could not be loaded.']}
		</SC.ErrorContainer>
	)
}

export const AdvancedMarkerWithRef = React.memo(
	(
		props: AdvancedMarkerProps & {
			markerID: string
			onMarkerClick: (e: google.maps.MapMouseEvent) => void
			setMarkerRef: (marker: Marker | null, id: string) => void
		}
	) => {
		const { children, onMarkerClick, setMarkerRef, markerID, ...advancedMarkerProps } = props

		const ref = useCallback((marker: google.maps.marker.AdvancedMarkerElement) => setMarkerRef(marker, markerID), [setMarkerRef, markerID])

		return (
			<AdvancedMarker {...advancedMarkerProps} ref={ref} onClick={onMarkerClick}>
				{children}
			</AdvancedMarker>
		)
	}
)

const markerCluster: Renderer['render'] = (cluster, _stats, mapArg) => {
	const { count, position } = cluster
	const { formatMessage } = getIntl()

	// change color if this cluster has more markers than the mean cluster
	const color = '#000000'
	// create svg literal with fill color
	const svg = `<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="50" height="50">
<circle cx="120" cy="120" opacity=".6" r="70" />
<circle cx="120" cy="120" opacity=".3" r="90" />
<circle cx="120" cy="120" opacity=".2" r="110" />
<text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="50" dominant-baseline="middle" font-family="roboto,arial,sans-serif">${count}</text>
</svg>`
	const title = formatMessage({ id: 'Cluster of {count} markers' }, { count })
	// adjust zIndex to be above other markers
	const zIndex = Number(google.maps.Marker.MAX_ZINDEX) + count
	if (MarkerUtils.isAdvancedMarkerAvailable(mapArg)) {
		// create cluster SVG element
		const parser = new DOMParser()
		const svgEl = parser.parseFromString(svg, 'image/svg+xml').documentElement
		svgEl.setAttribute('transform', 'translate(0 25)')
		const clusterOptions = {
			map: mapArg,
			position,
			zIndex,
			title,
			content: svgEl
		}
		return new google.maps.marker.AdvancedMarkerElement(clusterOptions)
	}
	const clusterOptions = {
		position,
		zIndex,
		title,
		icon: {
			url: `data:image/svg+xml;base64,${btoa(svg)}`,
			anchor: new google.maps.Point(25, 25)
		}
	}
	return new google.maps.Marker(clusterOptions)
}

// component
const GoogleMaps = forwardRef(<MarkerExtraType extends Record<string, any> = {}>(props: Props<MarkerExtraType>, ref: React.Ref<Handle>) => {
	const { markersLoading, children, markers, onMarkerClick, onClick, onCameraChanged, defaultSelectedMarkerID, ...restMapProps } = props
	const { assetsPath, googleMapId } = useContext(AppContext)

	const map = useMap()

	const loadingStatus = useApiLoadingStatus()

	const isMapLoaded = loadingStatus === APILoadingStatus.LOADED
	const isLoading = loadingStatus === APILoadingStatus.NOT_LOADED || loadingStatus === APILoadingStatus.LOADING || markersLoading
	const isError = loadingStatus === APILoadingStatus.FAILED || loadingStatus === APILoadingStatus.AUTH_FAILURE

	const [currentMarkerID, setCurrentMarkerID] = useState<string | null>(defaultSelectedMarkerID ?? null)

	const [clusterMarkers, setClusterMarkers] = useState<{ [key: string]: Marker }>({})

	const setMarkerRef = useCallback((marker: Marker | null, key: string) => {
		setClusterMarkers((currentMarkerClusters) => {
			if ((marker && currentMarkerClusters[key]) || (!marker && !currentMarkerClusters[key])) return currentMarkerClusters

			if (marker) {
				return { ...currentMarkerClusters, [key]: marker }
			}
			const { [key]: _, ...newMarkers } = currentMarkerClusters

			return newMarkers
		})
	}, [])

	const handleClick = (e: MapMouseEvent) => {
		if (currentMarkerID !== defaultSelectedMarkerID) {
			setCurrentMarkerID(null)
		}

		if (onClick) {
			onClick(e)
		}
	}

	useImperativeHandle(ref, () => ({
		resetCurrentMarker() {
			setCurrentMarkerID(null)
		}
	}))

	const clusterer = useMemo(() => {
		if (!map) return null

		try {
			return new MarkerClusterer({
				map,
				algorithmOptions: {
					maxZoom: 15
				},
				renderer: {
					render: markerCluster
				}
			})
		} catch {
			return null
		}
	}, [map])

	useEffect(() => {
		if (!clusterer) return

		clusterer.clearMarkers()
		clusterer.addMarkers(Object.values(clusterMarkers))
	}, [clusterer, clusterMarkers])

	/**
	 * Note: loadingStatus from @vis.gl/react-google-maps returns states in sequence: NOT_LOADED -> LOADING -> LOADED
	 * If there's an error during map initialization, the state changes to FAILED or AUTH_FAILURE only after reaching LOADED first
	 * This transition to LOADED before error states causes issues with Advanced Markers, which throw errors during that brief LOADED period and crash the application
	 * This problem is fixed by using the <ErrorBoundary> component, which catches the error and prevents the application from crashing
	 * GitHub issue: https://github.com/visgl/react-google-maps/issues/424
	 * JIRA issue: https://goodrequest.atlassian.net/browse/NOT-11898
	 */
	return (
		<SC.MapContainer>
			<ErrorBoundary FallbackComponent={ErrorComponent}>
				{isMapLoaded && (
					<Map
						defaultZoom={MAP.defaultZoom}
						maxZoom={MAP.maxZoom}
						minZoom={MAP.minZoom}
						zoomControl={false}
						streetViewControl={false}
						fullscreenControl={false}
						mapTypeControl={false}
						clickableIcons={false} // don't allow click on google maps default pins (e.g. pins for restaurants, places, ...)
						mapId={googleMapId}
						onClick={handleClick}
						{...restMapProps}
					>
						{markers?.map((markerData, index) => {
							const icon =
								markerData.id === currentMarkerID
									? `${assetsPath}/${require('../../assets/icons/location-pin-icon-pink.png')}`
									: `${assetsPath}/${require('../../assets/icons/location-pin-icon-black.png')}`

							return (
								<AdvancedMarkerWithRef
									key={index}
									markerID={markerData.id}
									position={markerData.position}
									onMarkerClick={() => {
										if (onMarkerClick) {
											onMarkerClick(clusterMarkers[markerData.id], markerData)
										}
										setCurrentMarkerID(markerData.id)
									}}
									setMarkerRef={setMarkerRef}
								>
									<img width={36} height={44} src={icon} alt={''} />
								</AdvancedMarkerWithRef>
							)
						})}
						{/* additional map elements */}
						{children}
					</Map>
				)}
				{isLoading && (
					<SC.LoadingContainer>
						<Spinner color={'icon.highlight'} size={24} />
					</SC.LoadingContainer>
				)}
				{isError && <ErrorComponent />}
			</ErrorBoundary>
		</SC.MapContainer>
	)
}) as <MarkerExtraType extends Record<string, any> = {}>(props: Props<MarkerExtraType> & { ref?: React.Ref<Handle> }) => JSX.Element

export default GoogleMaps
